import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionState } from '../hooks/useSessionState';
import FiltroPanel from '../components/FiltroPanel';
import RibbonActions from '../components/RibbonActions';
import { DEPARTAMENTOS, MUNICIPIOS } from '../data/dianUbicacion';
import {
  getClientes, createCliente, updateCliente, deleteCliente,
  getContactos, createContacto, updateContacto, deleteContacto,
  getCorreoRecepcion,
} from '../services/api';

const TIPOS_DOC = [['31', 'NIT'], ['13', 'Cédula de ciudadanía'], ['22', 'Cédula de extranjería'], ['41', 'Pasaporte'], ['12', 'Tarjeta de identidad'], ['50', 'NIT de otro país']];
const RESP_FISCAL = [['R-99-PN', 'No aplica / consumidor final'], ['O-49', 'No responsable de IVA'], ['O-48', 'Responsable de IVA'], ['O-47', 'Régimen simple de tributación'], ['O-13', 'Gran contribuyente'], ['O-15', 'Autorretenedor'], ['O-23', 'Agente de retención IVA']];
const EMPTY_FORM = { razon_social:'', nit:'', dv:'', tipo_documento:'31', email:'', telefono:'', ciudad:'', direccion:'', departamento:'', codigo_departamento:'', codigo_municipio:'', codigo_postal:'', responsabilidad_fiscal:'R-99-PN', contacto_nombre:'', contacto_cargo:'' };

// Dígito de verificación del NIT (algoritmo DIAN).
function calcularDV(nit) {
  const s = String(nit || '').replace(/\D/g, '');
  if (!s) return '';
  const pesos = [3, 7, 13, 17, 19, 23, 29, 37, 41, 43, 47, 53, 59, 67, 71];
  const d = s.split('').reverse();
  let sum = 0;
  for (let i = 0; i < d.length; i++) sum += parseInt(d[i], 10) * (pesos[i] || 0);
  const r = sum % 11;
  return String(r < 2 ? r : 11 - r);
}
const EMPTY_C    = { nombre:'', cargo:'', email:'', telefono:'', es_principal:false };

export default function Clientes() {
  const navigate = useNavigate();
  const [lista,    setLista]   = useState([]);
  const [search,   setSearch]  = useSessionState('cli_search', '');
  const [modal,    setModal]   = useState(false);
  const [editing,  setEditing] = useState(null);
  const [form,     setForm]    = useState(EMPTY_FORM);
  const [saving,   setSaving]  = useState(false);
  const [correoRec, setCorreoRec] = useState(null); // { email } del directorio DIAN por NIT

  // Busca el correo de recepción DIAN por NIT y lo precarga si el email está vacío.
  const buscarCorreoRec = async () => {
    const nit = (form.nit || '').replace(/\D/g, '');
    if (nit.length < 6) { setCorreoRec(null); return; }
    try {
      const { data } = await getCorreoRecepcion(nit);
      if (data.encontrado && data.email) {
        setCorreoRec(data.email);
        setForm(f => (f.email ? f : { ...f, email: data.email }));
      } else { setCorreoRec(null); }
    } catch { setCorreoRec(null); }
  };

  // Modal contactos
  const [cModal,      setCModal]      = useState(false);
  const [cCliente,    setCCliente]    = useState(null);   // cliente activo en modal contactos
  const [contactos,   setContactos]   = useState([]);
  const [cForm,       setCForm]       = useState(null);   // null=cerrado | {}=nuevo | {id,..}=editando
  const [savingC,     setSavingC]     = useState(false);

  const load = () => getClientes(search ? { search } : {}).then(r => setLista(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); }, [search]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setCorreoRec(null); setModal(true); };
  const openEdit   = (c) => { setEditing(c); setForm(c); setCorreoRec(null); setModal(true); };

  const handleSave = async () => {
    if (!form.razon_social || !form.nit) return alert('Razon social y NIT son requeridos');
    setSaving(true);
    const payload = { ...form, dv: form.dv || calcularDV(form.nit) };
    try {
      editing ? await updateCliente(editing.id, payload) : await createCliente(payload);
      setModal(false); load();
    } catch (e) { alert(e.response?.data?.error || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Desactivar este cliente?')) return;
    await deleteCliente(id); load();
  };

  // ── Contactos ───────────────────────────────────────────────────────────────
  const openContactos = (cliente) => {
    setCCliente(cliente);
    setCForm(null);
    getContactos(cliente.id).then(r => setContactos(r.data)).catch(() => setContactos([]));
    setCModal(true);
  };

  const handleSaveContacto = async () => {
    if (!cForm.nombre) return alert('Nombre es requerido');
    setSavingC(true);
    try {
      cForm.id
        ? await updateContacto(cCliente.id, cForm.id, cForm)
        : await createContacto(cCliente.id, cForm);
      getContactos(cCliente.id).then(r => setContactos(r.data));
      setCForm(null);
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
    finally { setSavingC(false); }
  };

  const handleDeleteContacto = async (cid) => {
    if (!window.confirm('Eliminar contacto?')) return;
    await deleteContacto(cCliente.id, cid);
    getContactos(cCliente.id).then(r => setContactos(r.data));
  };

  return (
    <div>
      <div style={s.pageHeader}>
        <div><h1 style={s.h1}>Clientes</h1><p style={s.sub}>Base de datos de clientes</p></div>
        <RibbonActions><div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <FiltroPanel
            fields={[{ tipo: 'input', label: 'Buscar', value: search, onChange: setSearch, placeholder: 'Razón social o NIT…', width: 230 }]}
            onClear={() => setSearch('')}
          />
          <button style={s.btnPrimary} onClick={openCreate}>+ Nuevo cliente</button>
        </div></RibbonActions>
      </div>
      <div style={s.card}>
        <table style={s.table}>
          <thead>
            <tr>{['Razon social','NIT','Correo FE','Telefono','Ciudad','Contacto',''].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {lista.length === 0 && <tr><td colSpan={7} style={s.empty}>Sin clientes registrados</td></tr>}
            {lista.map(c => (
              <tr key={c.id} style={s.tr}>
                <td style={{ ...s.td, fontWeight:600, color:'#93c5fd', cursor:'pointer' }}
                  onClick={() => navigate(`/clientes/${c.id}`)} title="Ver ficha del cliente">{c.razon_social}</td>
                <td style={s.td}>{c.nit}</td>
                <td style={s.td}>{c.email || '-'}</td>
                <td style={s.td}>{c.telefono || '-'}</td>
                <td style={s.td}>{c.ciudad || '-'}</td>
                <td style={s.td}>{c.contacto_nombre || '-'}</td>
                <td style={s.td}>
                  <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
                    <button style={s.editBtn}    title="Editar"     onClick={() => openEdit(c)}>✏️</button>
                    <button style={s.contactBtn} title="Contactos"  onClick={() => openContactos(c)}>👥</button>
                    <button style={s.deleteBtn}  title="Desactivar" onClick={() => handleDelete(c.id)}>🚫</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Modal cliente ─────────────────────────────────────────────────── */}
      {modal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>{editing ? 'Editar cliente' : 'Nuevo cliente'}</h2>
              <button style={s.closeBtn} onClick={() => setModal(false)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.grid2}>
                <div style={s.field}>
                  <label style={s.label}>Razon social *</label>
                  <input style={s.input} value={form.razon_social||''} onChange={e => setForm({...form,razon_social:e.target.value})} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>Tipo de documento</label>
                  <select style={s.input} value={form.tipo_documento || '31'} onChange={e => setForm({ ...form, tipo_documento:e.target.value })}>
                    {TIPOS_DOC.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <div style={{ ...s.field, flex:1 }}>
                    <label style={s.label}>{form.tipo_documento === '31' ? 'NIT *' : 'Número documento *'}</label>
                    <input style={s.input} value={form.nit||''} onChange={e => { const nit = e.target.value; setForm({...form, nit, dv: calcularDV(nit)}); }} onBlur={buscarCorreoRec} title="Al salir se busca el correo de recepción DIAN por el NIT" />
                  </div>
                  <div style={{ ...s.field, width:64 }}>
                    <label style={s.label}>DV</label>
                    <input style={{ ...s.input, textAlign:'center', fontWeight:700, color:'#93c5fd', background:'#0f172a' }} value={form.dv || calcularDV(form.nit)} readOnly title="Dígito de verificación (automático)" />
                  </div>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Correo de facturación electrónica</label>
                  <input style={s.input} type="email" value={form.email||''} onChange={e => setForm({...form,email:e.target.value})} placeholder="facturacion@empresa.com" />
                  <div style={{ color:'#64748b', fontSize:11, marginTop:3 }}>Va en el XML y el PDF de la factura y es el destinatario por defecto. El contacto, compras, etc. agrégalos abajo como contactos.</div>
                  {correoRec && (
                    <span style={{ fontSize:11, color: form.email === correoRec ? '#34d399' : '#93c5fd', marginTop:3, display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                      📩 Recepción DIAN: {correoRec}
                      {form.email !== correoRec && <button type="button" onClick={() => setForm(f => ({ ...f, email: correoRec }))} style={{ background:'none', border:'1px solid #334155', borderRadius:5, color:'#93c5fd', fontSize:10, padding:'1px 6px', cursor:'pointer' }}>usar</button>}
                    </span>
                  )}
                </div>
                <div style={s.field}>
                  <label style={s.label}>Telefono</label>
                  <input style={s.input} value={form.telefono||''} onChange={e => setForm({...form,telefono:e.target.value})} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>Departamento</label>
                  <select style={s.input} value={form.codigo_departamento || ''}
                    onChange={e => { const d = DEPARTAMENTOS.find(x => x.code === e.target.value);
                      setForm({ ...form, codigo_departamento:e.target.value, departamento:d ? d.name : '', codigo_municipio:'', ciudad:'' }); }}>
                    <option value="">— Seleccionar —</option>
                    {DEPARTAMENTOS.map(d => <option key={d.code} value={d.code}>{d.name}</option>)}
                  </select>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Municipio / Ciudad</label>
                  <select style={s.input} value={form.codigo_municipio || ''} disabled={!form.codigo_departamento}
                    onChange={e => { const m = MUNICIPIOS.find(x => x.code === e.target.value);
                      setForm({ ...form, codigo_municipio:e.target.value, ciudad:m ? m.name : '' }); }}>
                    <option value="">{form.codigo_departamento ? '— Seleccionar —' : 'Elige departamento'}</option>
                    {MUNICIPIOS.filter(m => m.code.slice(0, 2) === form.codigo_departamento).map(m => <option key={m.code} value={m.code}>{m.name}</option>)}
                  </select>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Código postal</label>
                  <input style={s.input} value={form.codigo_postal||''} placeholder="Ej: 110111" onChange={e => setForm({...form, codigo_postal:e.target.value})} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>Responsabilidad fiscal</label>
                  <select style={s.input} value={form.responsabilidad_fiscal || 'R-99-PN'} onChange={e => setForm({ ...form, responsabilidad_fiscal:e.target.value })}>
                    {RESP_FISCAL.map(([v, l]) => <option key={v} value={v}>{v} — {l}</option>)}
                  </select>
                </div>
                {[['contacto_nombre','Nombre contacto'],['contacto_cargo','Cargo contacto']].map(([k,label]) => (
                  <div key={k} style={s.field}>
                    <label style={s.label}>{label}</label>
                    <input style={s.input} value={form[k]||''} onChange={e => setForm({...form,[k]:e.target.value})} />
                  </div>
                ))}
                <div style={{ ...s.field, gridColumn:'1 / -1' }}>
                  <label style={s.label}>Dirección</label>
                  <input style={s.input} value={form.direccion||''} onChange={e => setForm({...form,direccion:e.target.value})} />
                </div>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={() => setModal(false)}>Cancelar</button>
              <button style={{ ...s.btnPrimary, opacity:saving?0.7:1 }} onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : editing ? 'Actualizar' : 'Crear cliente'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal contactos ───────────────────────────────────────────────── */}
      {cModal && cCliente && (
        <div style={s.overlay}>
          <div style={{ ...s.modal, maxWidth:620 }}>
            <div style={s.modalHeader}>
              <div>
                <h2 style={s.modalTitle}>👥 Contactos</h2>
                <p style={{ color:'#64748b', fontSize:12, margin:0 }}>{cCliente.razon_social}</p>
              </div>
              <button style={s.closeBtn} onClick={() => { setCModal(false); setCForm(null); }}>✕</button>
            </div>
            <div style={s.modalBody}>
              {/* Lista */}
              {contactos.length > 0 && !cForm && (
                <table style={{ ...s.table, marginBottom:16 }}>
                  <thead>
                    <tr>{['Nombre','Cargo','Email','Teléfono',''].map((h,i) => <th key={i} style={s.th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {contactos.map(ct => (
                      <tr key={ct.id}>
                        <td style={s.td}>
                          {ct.es_principal && <span style={s.principalBadge}>Principal</span>}
                          <span style={{ color:'#f1f5f9', fontWeight:ct.es_principal?600:400 }}>{ct.nombre}</span>
                        </td>
                        <td style={s.td}>{ct.cargo || '—'}</td>
                        <td style={{ ...s.td, wordBreak:'break-all' }}>{ct.email || '—'}</td>
                        <td style={{ ...s.td, whiteSpace:'nowrap' }}>{ct.telefono || '—'}</td>
                        <td style={{ ...s.td, whiteSpace:'nowrap', textAlign:'right' }}>
                          <button style={s.ctAccion} onClick={() => setCForm({...ct})}>Editar</button>
                          <button style={{ ...s.ctAccion, borderColor:'#7f1d1d', color:'#fca5a5', marginLeft:6 }} onClick={() => handleDeleteContacto(ct.id)}>Eliminar</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {contactos.length === 0 && !cForm && (
                <p style={{ color:'#475569', textAlign:'center', padding:'12px 0 20px', fontSize:13 }}>
                  Este cliente no tiene contactos. Agregue el primero.
                </p>
              )}

              {/* Formulario */}
              {cForm ? (
                <div style={s.cFormBox}>
                  <p style={s.cFormTitle}>{cForm.id ? 'Editar contacto' : 'Nuevo contacto'}</p>
                  <div style={s.grid2}>
                    <div style={s.field}>
                      <label style={s.label}>Nombre *</label>
                      <input style={s.input} value={cForm.nombre||''} onChange={e => setCForm({...cForm,nombre:e.target.value})} />
                    </div>
                    <div style={s.field}>
                      <label style={s.label}>Cargo</label>
                      <input style={s.input} value={cForm.cargo||''} onChange={e => setCForm({...cForm,cargo:e.target.value})} />
                    </div>
                    <div style={s.field}>
                      <label style={s.label}>Email</label>
                      <input style={s.input} type="email" value={cForm.email||''} onChange={e => setCForm({...cForm,email:e.target.value})} />
                    </div>
                    <div style={s.field}>
                      <label style={s.label}>Teléfono</label>
                      <input style={s.input} value={cForm.telefono||''} onChange={e => setCForm({...cForm,telefono:e.target.value})} />
                    </div>
                  </div>
                  <label style={{ ...s.label, display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                    <input type="checkbox" checked={!!cForm.es_principal} onChange={e => setCForm({...cForm,es_principal:e.target.checked})} />
                    Marcar como contacto principal
                  </label>
                  <div style={{ display:'flex', gap:8, marginTop:16 }}>
                    <button style={s.btnSecondary} onClick={() => setCForm(null)}>Cancelar</button>
                    <button style={{ ...s.btnPrimary, opacity:savingC?0.7:1 }} onClick={handleSaveContacto} disabled={savingC}>
                      {savingC ? 'Guardando...' : 'Guardar contacto'}
                    </button>
                  </div>
                </div>
              ) : (
                <button style={s.btnPrimary} onClick={() => setCForm({...EMPTY_C})}>
                  + Agregar contacto
                </button>
              )}
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={() => { setCModal(false); setCForm(null); }}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  pageHeader:    { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 },
  h1:            { color:'#f1f5f9', fontSize:24, fontWeight:700, margin:'0 0 4px' },
  sub:           { color:'#64748b', fontSize:14, margin:0 },
  btnPrimary:    { background:'#1d4ed8', color:'#fff', border:'none', borderRadius:8, padding:'10px 20px', fontSize:14, fontWeight:600, cursor:'pointer' },
  btnSecondary:  { background:'#334155', color:'#cbd5e1', border:'none', borderRadius:8, padding:'10px 20px', fontSize:14, cursor:'pointer' },
  searchBar:     { marginBottom:16 },
  searchInput:   { height:38, padding:'0 14px', background:'#0f172a', border:'1px solid #334155', borderRadius:8, color:'#f1f5f9', fontSize:13, outline:'none', boxSizing:'border-box', width:'100%', maxWidth:380 },
  card:          { background:'#1e293b', borderRadius:12, overflow:'hidden' },
  table:         { width:'100%', borderCollapse:'collapse' },
  tr:            {},
  th:            { padding:'10px 14px', color:'#64748b', fontSize:12, textAlign:'left', borderBottom:'1px solid #334155', background:'#162032' },
  td:            { padding:'4px 14px', color:'#94a3b8', fontSize:13, borderBottom:'1px solid #1a2332', verticalAlign:'middle' },
  empty:         { padding:'32px', textAlign:'center', color:'#475569' },
  editBtn:       { background:'#1e293b', border:'1px solid #334155', borderRadius:6, width:26, height:26, cursor:'pointer', fontSize:13, display:'inline-flex', alignItems:'center', justifyContent:'center', lineHeight:1 },
  contactBtn:    { background:'#1e293b', border:'1px solid #334155', borderRadius:6, width:26, height:26, cursor:'pointer', fontSize:13, display:'inline-flex', alignItems:'center', justifyContent:'center', lineHeight:1 },
  deleteBtn:     { background:'#1e293b', border:'1px solid #7f1d1d', borderRadius:6, width:26, height:26, cursor:'pointer', fontSize:13, display:'inline-flex', alignItems:'center', justifyContent:'center', lineHeight:1 },
  ctAccion:      { background:'#1e293b', border:'1px solid #334155', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:12, color:'#93c5fd', whiteSpace:'nowrap' },
  overlay:       { position:'fixed', inset:0, background:'rgba(0,0,0,.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100 },
  modal:         { background:'#1e293b', borderRadius:16, width:'92%', maxWidth:580, maxHeight:'90vh', display:'flex', flexDirection:'column', border:'1px solid #334155' },
  modalHeader:   { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'13px 20px', borderBottom:'1px solid #334155' },
  modalTitle:    { color:'#f1f5f9', fontSize:16, fontWeight:700, margin:0 },
  closeBtn:      { background:'transparent', border:'none', color:'#64748b', fontSize:18, cursor:'pointer', lineHeight:1 },
  modalBody:     { padding:'14px 20px', overflowY:'auto', overflowX:'hidden', flex:1 },
  modalFooter:   { display:'flex', justifyContent:'flex-end', gap:10, padding:'12px 20px', borderTop:'1px solid #334155' },
  grid2:         { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 16px' },
  field:         { marginBottom:10 },
  label:         { display:'block', color:'#94a3b8', fontSize:12, marginBottom:4 },
  input:         { width:'100%', height:34, padding:'0 12px', background:'#0f172a', border:'1px solid #334155', borderRadius:8, color:'#f1f5f9', fontSize:13, boxSizing:'border-box', outline:'none' },
  principalBadge:{ background:'#1e3a5f', color:'#93c5fd', borderRadius:10, padding:'1px 8px', fontSize:11, fontWeight:600, marginRight:6 },
  cFormBox:      { background:'#0f172a', borderRadius:10, padding:'16px', border:'1px solid #334155' },
  cFormTitle:    { color:'#cbd5e1', fontSize:14, fontWeight:600, margin:'0 0 14px' },
};
